import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { masterDataSelector } from '../state';
import { Management, Region, RegionalManagement, Supervision } from '../types';

const supervisionsSort = (a: Supervision, b: Supervision) => (a.label > b.label ? 1 : -1);

const useManagement = (
  countryCode: string,
  regionalManagements: RegionalManagement[],
  regions: Region[],
  management: Management[]
) => {
  const masterData = useSelector(masterDataSelector);

  const supervisionsForDivisonCountry = useMemo(() => {
    const supervisions = masterData.loaded && masterData.data ? masterData.data.supervisions : [];
    if (!countryCode) return supervisions;
    return supervisions.filter(i => i.countryCode === countryCode).sort(supervisionsSort);
  }, [masterData.data, masterData.loaded, countryCode]);

  const regionalManagementIds = useMemo(
    () => new Set(regionalManagements.map(i => i.regionalMgmtId)),
    [regionalManagements]
  );
  const regionIds = useMemo(() => new Set(regions.map(i => i.regionId)), [regions]);
  const managementIds = useMemo(() => new Set(management.map(i => i.mgmtId)), [management]);

  const supervisionsForRegionalManagement = useMemo(() => {
    const supervisions = supervisionsForDivisonCountry;
    if (!regionalManagementIds.size) return supervisions;
    return supervisions
      .filter(r => regionalManagementIds.has(r.regionalMgmtId))
      .sort(supervisionsSort);
  }, [regionalManagementIds, supervisionsForDivisonCountry]);

  const supervisionsForRegion = useMemo(() => {
    const supervisions = supervisionsForRegionalManagement;
    if (!regionIds.size) return supervisions;
    return supervisions.filter(r => regionIds.has(r.regionId)).sort(supervisionsSort);
  }, [regionIds, supervisionsForRegionalManagement]);

  const supervisionsForManagement = useMemo(() => {
    const supervisions = supervisionsForRegion;
    if (!managementIds.size) return supervisions;
    return supervisions.filter(r => managementIds.has(r.mgmtId)).sort(supervisionsSort);
  }, [managementIds, supervisionsForRegion]);

  const disabled = !masterData.data;

  const options = supervisionsForManagement || [];

  return { options, disabled, loading: masterData.loading };
};

export default useManagement;
